import { useEffect, useRef, useState, type Dispatch } from "react";
import { Form, FormState } from "src/components/Form/Form";
import { useEnv } from "src/contexts/env.context";
import { useStripe } from "src/contexts/stripe.context";
import { useFeatureFlag, useFlagIdentity } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";
import type { CardHandle } from "@repo/stripe-sdk";
import type { WizardAction, WizardPayment } from "./types";
import { exportPaymentLinkFor } from "./exportPaymentLink";

// StepPayment owns the card form + the Pay button. The wizard's Modal.Actions
// on this step shows only Cancel/Back — the primary CTA lives inline with the
// card so loading state and error messaging stay co-located. Behind
// FLAGS.exportPaymentLink it also offers a Stripe hosted payment link as an
// alternative to the in-app card (the card path stays for sim testing).

export function StepPayment(props: {
  payment: WizardPayment;
  dispatch: Dispatch<WizardAction>;
  projectId: string | undefined;
}) {
  const env = useEnv();
  const stripe = useStripe();
  const { capture } = useFlagIdentity();
  const linkEnabled = useFeatureFlag(FLAGS.exportPaymentLink);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardHandleRef = useRef<CardHandle | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState(false);

  const price = (env.exportPriceCents / 100).toFixed(2);
  const currencyCode = env.exportPriceCurrency.toUpperCase();
  const payLabel = `Pay ${price} ${currencyCode}`;

  // Create the PaymentIntent + mount the card element exactly once. The wizard
  // reducer's PAYMENT_PENDING transition guards against duplicates if React
  // re-runs the effect (e.g. fast-refresh).
  useEffect(() => {
    let cancelled = false;
    let mounted: CardHandle | null = null;

    (async () => {
      if (props.payment.status === "idle") {
        try {
          const res = await fetch("/api/stripe/create-payment-intent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          if (!res.ok) throw new Error(`create intent failed (${res.status})`);
          const { clientSecret, paymentIntentId } = (await res.json()) as {
            clientSecret: string;
            paymentIntentId: string;
          };
          if (cancelled) return;
          props.dispatch({ type: "PAYMENT_PENDING", intentId: paymentIntentId, clientSecret });
        } catch (err) {
          if (cancelled) return;
          setCreateError(err instanceof Error ? err.message : "Failed to start payment");
          return;
        }
      }
      if (cardRef.current && !cardHandleRef.current) {
        mounted = await stripe.mountCardElement(cardRef.current);
        if (cancelled) {
          mounted.unmount();
          return;
        }
        cardHandleRef.current = mounted;
      }
    })();

    return () => {
      cancelled = true;
      cardHandleRef.current?.unmount();
      cardHandleRef.current = null;
      mounted?.unmount();
    };
    // We intentionally exclude `props.payment.status` from deps — the effect's
    // job is one-shot init. Once PAYMENT_PENDING flips the status, the effect
    // body's `status === "idle"` guard skips the create call on the resulting
    // re-run while still mounting the card if needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPay = async () => {
    if (!props.payment.clientSecret || isPaying) return;
    setIsPaying(true);
    capture("export_payment_attempted", { paymentIntentId: props.payment.intentId });

    const confirm = await stripe.confirmCardPayment(props.payment.clientSecret);
    if (confirm.status === "failed") {
      capture("export_payment_failed", {
        paymentIntentId: props.payment.intentId,
        code: confirm.code,
      });
      props.dispatch({ type: "PAYMENT_FAILED", error: confirm.message });
      setIsPaying(false);
      return;
    }

    // Backend re-verifies the PaymentIntent before we treat the payment as
    // authoritative. The client-side confirm result alone isn't trustworthy —
    // a hostile client could fake `succeeded`.
    try {
      const res = await fetch(
        `/api/stripe/payment-intent-status?id=${encodeURIComponent(confirm.paymentIntentId)}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`verify failed (${res.status})`);
      const data = (await res.json()) as { status: string; paymentIntentId: string };
      if (data.status !== "succeeded") {
        capture("export_payment_failed", {
          paymentIntentId: data.paymentIntentId,
          code: "backend_verification_mismatch",
          backendStatus: data.status,
        });
        props.dispatch({
          type: "PAYMENT_FAILED",
          error: "Server could not confirm the payment. Please try again.",
        });
        setIsPaying(false);
        return;
      }
      capture("export_payment_succeeded", { paymentIntentId: data.paymentIntentId });
      props.dispatch({ type: "PAYMENT_SUCCEEDED" });
    } catch (err) {
      capture("export_payment_failed", {
        paymentIntentId: confirm.paymentIntentId,
        code: "backend_verification_error",
      });
      props.dispatch({
        type: "PAYMENT_FAILED",
        error: err instanceof Error ? err.message : "Verification request failed",
      });
      setIsPaying(false);
    }
  };

  if (createError) {
    return (
      <div className="min-w-[420px] text-body-1 text-red-700">
        Couldn&apos;t start payment: {createError}
      </div>
    );
  }

  const showError = props.payment.status === "failed" && props.payment.error;
  const ready = props.payment.status === "pending" && !!props.payment.clientSecret;
  const finalisingDownload = props.payment.status === "succeeded";

  return (
    <div className="flex flex-col space-y-3 min-w-[420px]">
      <div className="text-body-1 text-neutral-8">
        One-time charge: <span className="font-semibold">{`${price} ${currencyCode}`}</span>
      </div>
      <div className="flex flex-col space-y-1">
        <span className="text-neutral-8 text-caption">Card details</span>
        <div
          ref={cardRef}
          data-testid="stripe-card-mount"
          className="rounded-sm ring-neutral-6 ring-[1px] p-2"
        />
      </div>
      {showError && (
        <div className="text-body-2 text-red-700" data-testid="stripe-payment-error">
          {props.payment.error}
        </div>
      )}
      <Form.SubmitButton
        label={finalisingDownload ? "Generating PDF…" : payLabel}
        onClick={onPay}
        onEnterKey={onPay}
        loading={isPaying || finalisingDownload}
        state={ready && !isPaying && !finalisingDownload ? FormState.NONE : FormState.DISABLED}
      />
      {linkEnabled && (
        <>
          <div className="text-caption text-neutral-6 text-center">or</div>
          <a
            href={exportPaymentLinkFor(props.projectId)}
            onClick={() => capture("export_payment_link_opened", { projectId: props.projectId })}
            className="inline-flex h-9 items-center justify-center rounded-sm border border-neutral-6 text-body-1 text-neutral-8 hover:bg-neutral-2"
          >
            Pay with link
          </a>
          <div className="text-caption text-neutral-6 text-center">
            You&apos;ll be sent to Stripe and returned here to download your report.
          </div>
        </>
      )}
    </div>
  );
}
